import React, { forwardRef } from 'react'
import { Link, type LinkProps } from '@remix-run/react'
import {
  Transition,
  Menu as HeadlessMenu,
  type MenuProps,
  type MenuButtonProps,
  type MenuItemsProps,
  type MenuItemProps,
} from '@headlessui/react'
import { classNames } from '~/helpers/string'

function cn(defaultClass: string, className?: string | ((bag: any) => string)) {
  return typeof className === 'function'
    ? (bag: any) => classNames(defaultClass, className(bag))
    : classNames(defaultClass, className ?? '')
}

export const Menu = forwardRef<HTMLDivElement, MenuProps<'div'>>((props, ref) => {
  const { className, ...rest } = props
  return <HeadlessMenu as="div" ref={ref} className={cn('relative inline-block', className)} {...rest} />
})

export const MenuTrigger = forwardRef<HTMLButtonElement, MenuButtonProps<'button'>>((props, ref) => {
  const { className, children, ...rest } = props
  return (
    <HeadlessMenu.Button as={React.Fragment} ref={ref} className={cn('', className)} {...rest}>
      {children}
    </HeadlessMenu.Button>
  )
})

export const MenuContent = forwardRef<HTMLDivElement, MenuItemsProps<'div'>>((props, ref) => {
  const { className, ...rest } = props
  return (
    <Transition
      as={React.Fragment}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <HeadlessMenu.Items
        as="div"
        ref={ref}
        className={cn(
          'absolute right-0 z-10 mt-2 w-56 min-w-[150px] max-w-[300px] origin-top-right rounded-xl bg-white p-3 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
          className,
        )}
        {...rest}
      />
    </Transition>
  )
})

export const MenuItem = forwardRef<
  HTMLDivElement,
  MenuItemProps<typeof Link> &
    Omit<LinkProps, 'className' | 'children'> & {
      icon?: React.ReactNode
      iconPosition?: 'left' | 'right'
    }
>((props, ref) => {
  const { className, children, icon, iconPosition, ...rest } = props
  return (
    <HeadlessMenu.Item
      as={Link}
      ref={ref}
      className={(p) =>
        classNames(
          'flex w-full flex-row items-center gap-2 rounded-md px-3 py-2 text-sm text-base-content',
          p.active ? 'bg-primary-150' : '',
          typeof className === 'function' ? className(p) : (className ?? ''),
        )
      }
      {...rest}
    >
      {(p) => (
        <>
          {icon && iconPosition === 'left' && icon}
          {typeof children === 'function' ? children(p) : children}
          {icon && iconPosition === 'right' && icon}
        </>
      )}
    </HeadlessMenu.Item>
  )
})
